import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { RoleFormValues, RoleOverview } from '../types';
import { ROLE_CODE_PATTERN, roleCodeFromName } from '../utils/matrix';

export type RoleFormMode =
  | { kind: 'create' }
  | { kind: 'duplicate'; source: RoleOverview }
  | { kind: 'edit'; role: RoleOverview };

interface RoleFormDialogProps {
  mode: RoleFormMode | null;
  onOpenChange: (open: boolean) => void;
  busy: boolean;
  onSubmit: (values: RoleFormValues) => void;
}

const TEXT = {
  create: { title: 'Nuevo rol', submit: 'Crear rol', hint: 'Después de crearlo podrás elegir sus permisos.' },
  duplicate: { title: 'Duplicar rol', submit: 'Crear copia', hint: 'El nuevo rol empieza con los mismos permisos.' },
  edit: { title: 'Editar rol', submit: 'Guardar', hint: 'El código no se puede cambiar.' },
};

function defaults(mode: RoleFormMode | null): RoleFormValues {
  if (mode?.kind === 'edit') {
    const r = mode.role;
    return { code: r.code, name: r.name, description: r.description ?? '', is_active: r.is_active };
  }
  if (mode?.kind === 'duplicate') {
    const name = `Copia de ${mode.source.name}`;
    return { code: roleCodeFromName(name), name, description: mode.source.description ?? '', is_active: true };
  }
  return { code: '', name: '', description: '', is_active: true };
}

export function RoleFormDialog({ mode, onOpenChange, busy, onSubmit }: RoleFormDialogProps) {
  const [v, setV] = useState<RoleFormValues>(defaults(mode));
  // While untouched, the code follows the name
  const [codeTouched, setCodeTouched] = useState(false);

  useEffect(() => {
    if (!mode) return;
    setV(defaults(mode));
    setCodeTouched(false);
  }, [mode]);

  const isEdit = mode?.kind === 'edit';
  const isSystem = isEdit && mode.role.is_system;
  const text = TEXT[mode?.kind ?? 'create'];

  const errors = {
    name: v.name.trim().length < 3 ? 'Escribe un nombre (mínimo 3 letras).' : null,
    code: !isEdit && !ROLE_CODE_PATTERN.test(v.code)
      ? 'Mayúsculas, números y guion bajo; empieza con letra (ej. TESORERO_DISTRITAL).'
      : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invalid && !busy) onSubmit({ ...v, name: v.name.trim(), description: v.description.trim() });
  };

  return (
    <Dialog open={!!mode} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{text.title}</DialogTitle>
            <DialogDescription>{text.hint}</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="role-name">Nombre</Label>
            <Input
              id="role-name"
              autoFocus
              value={v.name}
              placeholder="Ej. Tesorero Distrital"
              onChange={(e) => {
                const name = e.target.value;
                setV((p) => ({ ...p, name, code: !isEdit && !codeTouched ? roleCodeFromName(name) : p.code }));
              }}
            />
            {errors.name && v.name !== '' && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="role-code">Código</Label>
            <Input
              id="role-code"
              className="font-mono"
              value={v.code}
              disabled={isEdit}
              placeholder="TESORERO_DISTRITAL"
              onChange={(e) => {
                setCodeTouched(true);
                setV((p) => ({ ...p, code: e.target.value.toUpperCase() }));
              }}
            />
            {errors.code && v.code !== '' && <p className="text-xs text-destructive">{errors.code}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="role-desc">Descripción (opcional)</Label>
            <Textarea
              id="role-desc"
              rows={2}
              value={v.description}
              placeholder="Para qué sirve este rol"
              onChange={(e) => setV((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          {isEdit && (
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div>
                <Label htmlFor="role-active">Rol activo</Label>
                <p className="text-xs text-muted-foreground">
                  {isSystem
                    ? 'Los roles de sistema siempre están activos.'
                    : 'Si lo desactivas, quienes lo tienen pierden sus permisos hasta reactivarlo.'}
                </p>
              </div>
              <Switch
                id="role-active"
                checked={v.is_active}
                disabled={isSystem}
                onCheckedChange={(is_active) => setV((p) => ({ ...p, is_active }))}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={invalid || busy}>
              {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
              {text.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
